import axios from "axios";

/**
 * Checks if a JWT token was issued before the most recent 12:00 AM IST.
 * If issued before today's 12:00 AM IST, the session is expired according to the midnight reset policy.
 */
export const isTokenExpiredMidnight = (token) => {
  if (!token || typeof token !== "string") return false;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;
    let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) {
      base64 += "=";
    }
    const payload = JSON.parse(decodeURIComponent(escape(atob(base64))));
    if (!payload || !payload.iat) return false;

    const tokenIssueTime = payload.iat * 1000;

    // Current time in Asia/Kolkata
    const now = new Date();
    const kolkataStr = now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" });
    const kolkataDate = new Date(kolkataStr);
    kolkataDate.setHours(0, 0, 0, 0);

    // Convert Kolkata midnight back to UTC timestamp
    const midnightEpoch =
      kolkataDate.getTime() -
      kolkataDate.getTimezoneOffset() * 60000 -
      5.5 * 3600 * 1000;

    return tokenIssueTime < midnightEpoch;
  } catch (e) {
    return false;
  }
};

export const handleSessionExpired = () => {
  const currentPath = window.location.pathname;
  const isAuthOrPublicPage =
    currentPath === "/login-area" ||
    currentPath.startsWith("/Signup") ||
    currentPath === "/privacy-policy" ||
    currentPath === "/refund-policy" ||
    currentPath === "/terms-condition";

  window.localStorage.clear();
  window.sessionStorage.clear();

  if (!isAuthOrPublicPage) {
    window.location.href = "/login-area";
  }
};

const isExpiredPayload = (data) => {
  if (!data) return false;
  const is401 =
    data.statusCode === "401" ||
    data.status === "401" ||
    data.statusCode === 401 ||
    data.status === 401;
  const isExpiredMsg =
    data.message === "Session expired. Please login again." ||
    data.message === "Token expire" ||
    data.message === "Token expired" ||
    data.message === "Auth Failed" ||
    data.message === "Unauthorized";
  return is401 && isExpiredMsg;
};

const requestMiddleWare = (config) => {
  const token = window.localStorage.getItem("userToken");
  const headers = {
    "content-type": "application/json; charset=UTF-8",
    authorization: `Bearer ${token}`,
    ...config.headers, // Spread existing headers from config
  };

  // Merge the headers into the config
  return { ...config, headers };
};

const responseMiddleWare = (response) => {
  const data = response?.data;
  if (isExpiredPayload(data)) {
    handleSessionExpired();
  }
  return response.data;
};

const errorHandlerMiddleWare = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (status === 401 || isExpiredPayload(data)) {
    handleSessionExpired();
  }
  return Promise.reject(error);
};

// Automatically attach to default global axios instance so ALL 350+ API functions in Login.js are intercepted!
axios.interceptors.response.use(
  (response) => {
    const data = response?.data;
    if (isExpiredPayload(data)) {
      handleSessionExpired();
    }
    return response;
  },
  (error) => {
    errorHandlerMiddleWare(error);
    return Promise.reject(error);
  }
);

export { requestMiddleWare, responseMiddleWare, errorHandlerMiddleWare };
